import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { CheckCircle2, Lock } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useCart } from '@/lib/cart';
import { toast } from 'sonner';

export default function Checkout() {
    const { type, loading, user, refresh } = useAuth();
    const { cart, refresh: refreshCart } = useCart();
    const navigate = useNavigate();
    const [form, setForm] = useState({
        address: '', city: user?.city || '', pincode: '', phone: user?.mobile || '',
        fulfilment: 'delivery', apply_points: 0,
    });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(null);

    useEffect(() => { if (user) setForm((f) => ({ ...f, phone: f.phone || user.mobile || '', city: f.city || user.city || '' })); }, [user]);

    if (loading) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
    if (type !== 'customer') return <Navigate to="/login?next=/checkout" replace />;

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const pointsValue = (Number(form.apply_points) || 0) * 0.5;
    const maxPoints = Math.min(user?.points_balance || 0, Math.floor((cart.total || 0) / 0.5));
    const finalPayable = Math.max(0, (cart.total || 0) - pointsValue);

    const place = async () => {
        if (!form.address || !form.city || !form.pincode || !form.phone) { toast.error('Please complete address details'); return; }
        setBusy(true);
        try {
            const { data } = await api.post('/checkout', {
                ...form, apply_points: Number(form.apply_points) || 0,
            });
            setDone(data);
            await refresh();
            await refreshCart();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Checkout failed');
        } finally { setBusy(false); }
    };

    if (done) {
        return (
            <div className="min-h-screen flex items-center justify-center px-6 bg-cream pt-20" data-testid="checkout-success">
                <div className="max-w-lg bg-white p-12 text-center card-luxury">
                    <CheckCircle2 size={56} strokeWidth={1.5} className="text-inorbit-orange mx-auto mb-6" />
                    <div className="overline mb-3">Order Confirmed</div>
                    <h1 className="font-serif text-4xl text-inorbit-blue">Thank you, {user.name.split(' ')[0]}!</h1>
                    <p className="text-text-secondary mt-4">Your order has been placed across {done.orders.length} brand{done.orders.length > 1 ? 's' : ''}.</p>
                    <div className="mt-6 card-luxury p-5 text-left text-sm space-y-2 bg-cream">
                        {done.orders.map((o) => (
                            <div key={o.id} className="flex justify-between">
                                <span>{o.brand_name} • <span className="font-mono">{o.order_number}</span></span>
                                <span>₹{Math.round(o.total).toLocaleString('en-IN')}</span>
                            </div>
                        ))}
                        <div className="pt-3 border-t border-border-medium flex justify-between"><span>Total paid</span><span className="font-semibold">₹{Math.round(done.total_paid).toLocaleString('en-IN')}</span></div>
                        <div className="flex justify-between text-inorbit-orange"><span>Points earned</span><span className="font-semibold">+{done.points_earned}</span></div>
                    </div>
                    <div className="mt-8 flex gap-3 justify-center">
                        <button onClick={() => navigate('/orders')} className="btn-primary" data-testid="goto-orders-btn">View Orders</button>
                        <button onClick={() => navigate('/shop')} className="btn-outline" data-testid="continue-shopping-btn">Continue Shopping</button>
                    </div>
                </div>
            </div>
        );
    }

    if (cart.items.length === 0) {
        return (
            <div className="min-h-screen flex items-center justify-center pt-20 px-6">
                <div className="text-center">
                    <h1 className="font-serif text-3xl text-inorbit-blue">Your cart is empty</h1>
                    <button onClick={() => navigate('/shop')} className="btn-primary mt-6">Shop Now</button>
                </div>
            </div>
        );
    }

    return (
        <div className="pt-16 pb-32 bg-cream min-h-screen" data-testid="checkout-page">
            <div className="max-w-6xl mx-auto px-6 md:px-12 pt-10">
                <div className="overline mb-3">Secure Checkout</div>
                <h1 className="font-serif text-5xl text-inorbit-blue">Complete your order</h1>
            </div>

            <div className="max-w-6xl mx-auto px-6 md:px-12 mt-10 grid lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-6">
                    <div className="card-luxury p-8" data-testid="checkout-address">
                        <div className="overline mb-4">Delivery Address</div>
                        <div className="grid sm:grid-cols-2 gap-5">
                            <div className="sm:col-span-2">
                                <label className="text-xs uppercase tracking-widest text-text-tertiary block mb-2">Address</label>
                                <input className="input-box" value={form.address} onChange={set('address')} placeholder="Flat / Building / Street" data-testid="checkout-address-input" />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-widest text-text-tertiary block mb-2">City</label>
                                <input className="input-box" value={form.city} onChange={set('city')} data-testid="checkout-city-input" />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-widest text-text-tertiary block mb-2">Pincode</label>
                                <input className="input-box" value={form.pincode} onChange={set('pincode')} data-testid="checkout-pincode-input" />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-widest text-text-tertiary block mb-2">Phone</label>
                                <input className="input-box" value={form.phone} onChange={set('phone')} data-testid="checkout-phone-input" />
                            </div>
                        </div>
                    </div>

                    <div className="card-luxury p-8" data-testid="checkout-fulfilment">
                        <div className="overline mb-4">Fulfilment</div>
                        <div className="grid sm:grid-cols-2 gap-4">
                            {[{ v: 'delivery', t: 'Home Delivery', d: 'Delivered to your address in 2-3 days' }, { v: 'pickup', t: 'Mall Pickup', d: 'Collect from the store at the mall' }].map((o) => (
                                <button
                                    key={o.v}
                                    onClick={() => setForm({ ...form, fulfilment: o.v })}
                                    className={`text-left p-5 border-2 rounded ${form.fulfilment === o.v ? 'border-inorbit-orange bg-inorbit-yellow/10' : 'border-border-subtle hover:border-inorbit-blue'}`}
                                    data-testid={`fulfilment-${o.v}`}
                                >
                                    <div className="font-semibold text-inorbit-blue">{o.t}</div>
                                    <div className="text-xs text-text-tertiary mt-1">{o.d}</div>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="card-luxury p-8" data-testid="checkout-points">
                        <div className="overline mb-4">Use Inorbit One Points</div>
                        <div className="text-sm text-text-secondary mb-4">You have <span className="font-semibold text-inorbit-blue">{user.points_balance.toLocaleString()}</span> points • Worth ₹{((user.points_balance || 0) * 0.5).toLocaleString('en-IN')}</div>
                        <input
                            type="number" min="0" max={maxPoints}
                            value={form.apply_points} onChange={set('apply_points')}
                            placeholder="Points to redeem"
                            className="input-box" data-testid="apply-points-input"
                        />
                        {Number(form.apply_points) > 0 && <div className="text-xs text-inorbit-orange mt-2">₹{pointsValue.toLocaleString('en-IN')} off applied</div>}
                    </div>

                    <div className="card-luxury p-8" data-testid="checkout-payment">
                        <div className="overline mb-4">Payment Method</div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            {['UPI', 'Cards', 'Net Banking', 'Razorpay'].map((p) => (
                                <div key={p} className="p-4 border-2 border-inorbit-blue bg-inorbit-yellow/10 rounded text-center text-sm font-medium text-inorbit-blue">{p}</div>
                            ))}
                        </div>
                        <div className="text-xs text-text-tertiary mt-4 flex items-center gap-2"><Lock size={12} /> Payment integration is in pilot mode — orders are mock-paid for now</div>
                    </div>
                </div>

                <aside className="card-luxury p-8 h-fit sticky top-24" data-testid="checkout-summary">
                    <div className="overline mb-4">Order Summary</div>
                    <div className="space-y-3 mb-5">
                        {cart.items.map((it) => (
                            <div key={it.product_id} className="flex gap-3 text-sm">
                                <img src={it.image} alt="" className="w-14 h-16 object-cover" />
                                <div className="flex-1">
                                    <div className="text-inorbit-blue line-clamp-1">{it.name}</div>
                                    <div className="text-xs text-text-tertiary">{it.brand_name} × {it.quantity}</div>
                                </div>
                                <div className="text-right text-inorbit-blue font-medium">₹{(it.line_total).toLocaleString('en-IN')}</div>
                            </div>
                        ))}
                    </div>
                    <div className="border-t border-border-subtle pt-4 space-y-2 text-sm">
                        <div className="flex justify-between"><span>Subtotal</span><span>₹{cart.subtotal.toLocaleString('en-IN')}</span></div>
                        <div className="flex justify-between"><span>Delivery</span><span>{cart.delivery === 0 ? 'FREE' : `₹${cart.delivery}`}</span></div>
                        <div className="flex justify-between"><span>Tax</span><span>₹{cart.tax.toLocaleString('en-IN')}</span></div>
                        {Number(form.apply_points) > 0 && <div className="flex justify-between text-inorbit-orange"><span>Points discount</span><span>− ₹{pointsValue.toLocaleString('en-IN')}</span></div>}
                    </div>
                    <div className="border-t border-border-medium mt-3 pt-3 flex justify-between items-center">
                        <span className="text-sm uppercase tracking-widest">Total</span>
                        <span className="font-serif text-3xl text-inorbit-blue">₹{Math.round(finalPayable).toLocaleString('en-IN')}</span>
                    </div>
                    <button onClick={place} disabled={busy} className="btn-primary w-full justify-center mt-6" data-testid="place-order-btn">
                        {busy ? 'Placing order…' : 'Place Order'}
                    </button>
                </aside>
            </div>
        </div>
    );
}
